import { Box, InputGroup, Text } from '@chakra-ui/react';
import { ReactElement, useState, useRef, KeyboardEvent, ChangeEvent } from 'react';
import { UserIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { Input } from 'components/ui/form/Input/Input';
import { ChipInputChips } from './ChipInputChips';

type ChipInputProps = {
  placeholder?: string;
  /** Array of chip values (strings) */
  values: string[];
  /** Callback when chips change - receives array of chip strings */
  onChange: (values: string[]) => void;
  /** Callback when a chip is removed */
  onRemove?: (value: string) => void;
  width?: string | number;
  minWidth?: string | number;
  /** Maximum height before scrolling (in pixels or chakra units) */
  maxHeight?: string | number;
  /** Whether to allow duplicate chips */
  allowDuplicates?: boolean;
  /** Validation function - return false to prevent chip creation */
  validate?: (value: string) => boolean;
  /** Callback when validation error state changes */
  onValidationError?: (hasError: boolean) => void;
  /** External error state */
  hasError?: boolean;
  /** Error message to display */
  errorMessage?: string;
  /** Input type (default: 'text') */
  inputType?: string;
  /** Custom paste handler - if provided, overrides default paste behavior */
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  /** Controlled input value - if provided, component becomes controlled */
  inputValue?: string;
  /** Callback when input value changes (required if inputValue is provided) */
  onInputValueChange?: (value: string) => void;
};

/**
 * ChipInput component - A text input that creates chips from user input
 * Creates chips when user types space, comma, or presses Enter
 * Wraps and extends vertically for many entries
 *
 * @example
 * <ChipInput
 *   placeholder="Add tags..."
 *   values={tags}
 *   onChange={setTags}
 * />
 */
export const ChipInput = ({
  placeholder = 'Add items...',
  values = [],
  onChange,
  onRemove,
  width = '100%',
  minWidth = '100%',
  maxHeight = '200px',
  allowDuplicates = false,
  validate,
  onValidationError,
  hasError = false,
  errorMessage,
  inputType = 'text',
  onPaste: customPasteHandler,
  inputValue: controlledInputValue,
  onInputValueChange,
}: ChipInputProps): ReactElement => {
  const [internalInputValue, setInternalInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use controlled value if provided, otherwise use internal state
  const isControlled = controlledInputValue !== undefined;
  const inputValue = isControlled ? controlledInputValue : internalInputValue;

  const setInputValue = (value: string): void => {
    if (isControlled) {
      onInputValueChange?.(value);
    } else {
      setInternalInputValue(value);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setInputValue(value);
    // Clear error state while typing
    if (value.trim() && onValidationError) {
      onValidationError(false);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    if (customPasteHandler) {
      customPasteHandler(e);
      return;
    }

    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    // Create chips from pasted text
    const items = pastedText
      .split(/[,;\s\n]+/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    const newItems = allowDuplicates ? items : items.filter((item) => !values.includes(item));

    if (newItems.length > 0) {
      onChange([...values, ...newItems]);
    }
    setInputValue('');
  };

  const createChip = (value: string): boolean => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return false;
    }

    // Validate if validation function provided
    if (validate && !validate(trimmedValue)) {
      if (onValidationError) {
        onValidationError(true);
      }
      return false;
    }

    // Check for duplicates if not allowed
    if (!allowDuplicates && values.includes(trimmedValue)) {
      setInputValue('');
      if (onValidationError) {
        onValidationError(false);
      }
      return false;
    }

    // Add the new chip
    onChange([...values, trimmedValue]);
    setInputValue('');
    if (onValidationError) {
      onValidationError(false);
    }
    return true;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    // Create chip on Enter, Comma, or Space
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      createChip(inputValue);
    } else if (e.key === ' ' && inputValue.trim().length > 0) {
      // Only create chip on space if there's actual content
      e.preventDefault();
      createChip(inputValue);
    } else if (e.key === 'Backspace' && inputValue === '' && values.length > 0) {
      // Remove last chip when backspace is pressed on empty input
      e.preventDefault();
      const lastValue = values[values.length - 1];
      const newValues = values.slice(0, -1);
      onChange(newValues);
      onRemove?.(lastValue);
      if (onValidationError) {
        onValidationError(false);
      }
    }
  };

  const handleBlur = (): void => {
    setIsFocused(false);
    // Create chip from remaining input value on blur if it has content
    if (inputValue.trim()) {
      createChip(inputValue);
      // Note: createChip handles clearing input on success, but keeps invalid text
      // when validation fails, so users can correct it
    } else if (onValidationError) {
      onValidationError(false);
    }
  };

  const handleFocus = (): void => {
    setIsFocused(true);
  };

  const handleRemoveChip = ({
    id,
    label,
    e,
  }: {
    id: string;
    label: string;
    e: React.MouseEvent;
  }): void => {
    e.stopPropagation();
    const newValues = values.filter((v) => v !== id);
    onChange(newValues);
    onRemove?.(label);
    // Refocus input after removing chip
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const hasChips = values.length > 0;

  // Determine border color based on state
  const getBorderColor = (): string => {
    if (hasError) return 'text.error';
    if (isFocused) return 'border.accent.active';
    return 'border.accent';
  };

  return (
    <Box ref={containerRef} position="relative" width={width} minWidth={minWidth}>
      <InputGroup
        width="100%"
        startElement={<NovaIcon icon={UserIcon} size="s" color="text.quaternary" />}
      >
        <Box
          bg="background.secondary"
          border="default"
          borderColor={getBorderColor()}
          borderRadius="l"
          minH="48px"
          pl="40px !important"
          pr="15px"
          display="flex"
          flexWrap="wrap"
          alignItems="flex-start"
          gap="xs"
          py="10px"
          position="relative"
          maxH={maxHeight}
          overflowY="auto"
          width="100%"
          transition="border-color 0.2s"
          css={{
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#E5E5E5',
              borderRadius: '2px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#CCCCCC',
            },
          }}
        >
          {/* Chips */}
          {hasChips && (
            <ChipInputChips
              values={values.map((value) => ({ id: value, label: value }))}
              onRemove={handleRemoveChip}
            />
          )}

          {/* Input field */}
          <Box
            flexGrow={1}
            flexShrink={1}
            flexBasis="0"
            minW={hasChips ? '120px' : minWidth || '120px'}
          >
            <Input
              ref={inputRef}
              size="chip"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onPaste={handlePaste}
              type={inputType}
              border="none"
              outline="none"
              bg="transparent"
              h="28px"
              minH="28px"
              width="100%"
              _focus={{ boxShadow: 'none' }}
              _hover={{ border: 'none' }}
              textStyle="body.m.regular"
              color="text.primary"
              placeholder={!hasChips ? placeholder : ''}
            />
          </Box>
        </Box>
      </InputGroup>
      {/* Error message */}
      {hasError && errorMessage && (
        <Text textStyle="body.xs.regular" color="text.error" mt="4px" ml="40px">
          {errorMessage}
        </Text>
      )}
    </Box>
  );
};
